import { makeAutoObservable, runInAction } from 'mobx'

import _ from 'lodash'
// evertel
import { injectable, inject, decorate } from '@evertel/di'
import { Api } from '@evertel/api'
import { APIDataRoomMessage, APIDataThreadMessage } from '@evertel/types'
import moment from 'moment'
import { RoomStore, RoomMessagesStore, ThreadStore, ThreadMessagesStore, DisplayRoomMessage, DisplayThreadMessage } from '@evertel/stores'
import { FetchUserService, UnreadCountsState } from '@evertel/blue-user'
import { MediaUploadService } from '@evertel/media'
import { PushService, GPCPushNotification } from '@evertel/push'
import { PollConfig } from '@evertel/session/PollConfig'
import debugModule from 'debug'
const debug = debugModule('app:MessageWallController')
debug.enabled = false

const GET_ROOM_MESSAGE_POLL_INTERVAL_MS = 10000
const GET_THREAD_MESSAGE_POLL_INTERVAL_MS = 5000

export const MESSAGE_DEFAULT_INCLUDE = [{
    relation: 'media',
    scope: {
        fields: ['id', 'mimetype', 'url', 'ownerId', 'requiresAuth', 'description', 'previewUrl', 'fileName', 'contentLength', 'meta']
    }
}, {
    relation: 'reactions',
    scope: {
        include: [{
            relation: 'reactedByThrough',
            scope: {
                fields: ['blueUserId']
            }
        }]
    }
}]

class MessageWallController {
    modelId = null // roomId or threadId
    modelType: 'room' | 'thread' = 'room'
    messagesCount = null
    limit = 44
    errorFetching = null
    isActive = true // Track if app/tab is active, defaults to true for web compatibility

    private _activeView: 'primary' | 'anchor' = 'primary' // which window the user is currently viewing
    anchorMessageId: number | null = null                 // the message they jumped to

    get activeView(): 'primary' | 'anchor' {
        return this._activeView
    }
    set activeView(value: 'primary' | 'anchor') {
        this._activeView = value
    }

    private _repliesToId: number | null = null
    private pollInterval: any | undefined = undefined
    private messagesStore: RoomMessagesStore | ThreadMessagesStore = undefined

    private msgWallId: string = undefined

    constructor(
        private api: Api,
        private roomStore: RoomStore,
        private roomMessagesStore: RoomMessagesStore,
        private threadStore: ThreadStore,
        private threadMessagesStore: ThreadMessagesStore,
        private unreadState: UnreadCountsState,
        private mediaUploadService: MediaUploadService,
        private fetchUserService: FetchUserService,
        private pushService: PushService
    ) {
        makeAutoObservable(this)
    }

    init = (modelId: number, modelType: 'room' | 'thread', repliesToId: number | null = null, msgWallId = null) => {
        debug(`${msgWallId} init, ${modelType.toUpperCase()}#${modelId}`)
        if (!modelId) {
            console.error('Missing a modelId! in MessageWallController.init')
            return
        }

        this.msgWallId = msgWallId
        this.modelId = modelId
        this.modelType = modelType
        this.repliesToId = repliesToId
        this.messagesStore = (modelType === 'room') ? this.roomMessagesStore : this.threadMessagesStore

        // Restore anchor state if anchor-tagged messages exist in the store from a previous visit
        const existingAnchor = this.getMessagesForContextSorted('anchor')
        this.anchorMessageId = existingAnchor.length ? existingAnchor[0].id : null
        if (existingAnchor.length) {
            debug(`${this.msgWallId} 🔄 init: restored anchor state with ${existingAnchor.length} existing anchor messages, anchorMsg#${this.anchorMessageId}`)
        }

        this.pushService.addPushNotificationListener(this.onPushNotification)
        this.startPoll()
    }

    destroy = () => {
        debug(`${this.msgWallId} 🗑️ 🗑️ 🗑️ 🗑️ 🗑️ Destroy start: ${this.modelType?.toUpperCase()}#${this.modelId}, Interval#${this.pollInterval}`)
        this.pushService.removePushNotificationListener(this.onPushNotification)
        this.stopPoll()
        this.modelId = null
        this.modelType = null
        this.repliesToId = null
        this._activeView = 'primary'
        this.anchorMessageId = null
    }

    onPushNotification = (notification: GPCPushNotification) => {
        debug(`${this.msgWallId}🫸 onPushNotification: ${this.modelType.toUpperCase()}#${this.modelId}`)
        if (!this.api.isProduction) debug('MessageWallController.onPushNotification', notification)
        // If we get a push for the model and id we are looking at, update
        // TODO: make this smarter to only fetch what we know has changed.
        const pushRepliesToId = notification.data.repliesToId ?? null
        const wallRepliesToId = this.repliesToId ?? null
        if (notification.data.type === 'RoomMessage'
            && this.modelType === 'room'
            && notification.data.roomId === this.modelId
            && pushRepliesToId === wallRepliesToId
        ) {
            this.fetchMessages('fresh')
        } else if (notification.data.type === 'ThreadMessage' && this.modelType === 'thread' && notification.data.threadId === this.modelId) {
            this.fetchMessages('fresh')
        } else {
            this.unreadState.fetchUnreadCounts()
        }
    }

    setLimit(limit: number) {
        this.limit = limit
    }

    get repliesToId() {
        return this._repliesToId
    }

    set repliesToId(repliesToId) {
        this._repliesToId = repliesToId
        this.messagesCount = null
    }

    /**
     * @experimental don't use this probably, it's fetched with notification permissions in RoomController
     */
    get repliesToMsg() {
        if (this.messagesStore) {
            return this.messagesStore.findById(this.repliesToId)
        }
        return null
    }

    /**
     * Shared where clause for all message fetches.
     * Scopes to the current repliesToId and filters out unpublished messages.
     */
    private get sharedMessageWhere() {
        return {
            repliesToId: this.repliesToId,
            publishedDate: { neq: null },
            and: [
                {
                    or: [
                        // { isRetracted: false }
                        // { and: [{ isRetracted: true }, { hasReplies: true }] }
                    ]
                }
            ]
        }
    }

    /**
     * Fetch messages from the API, abstracting the room-vs-thread branching.
     * Single mock point for tests.
     */
    private fetchMessagesFromAPI(filter: any): Promise<(APIDataRoomMessage | APIDataThreadMessage)[]> {
        return this.modelType === 'room'
            ? this.api.Routes.Room.getMessages(this.modelId, filter)
            : this.api.Routes.Thread.getMessages(this.modelId, filter)
    }

    // ─── Shared Post-Fetch Pipeline ────────────────────────────────────

    /**
     * Shared post-fetch pipeline for all message ingestion paths.
     * Handles collision detection, merge, retracted normalization, window tagging,
     * store update, and user fetching.
     *
     * Both `fetchMessages` and `seedAnchorWindow` delegate here after their
     * respective API calls. This eliminates duplicated logic and ensures
     * consistent collision/merge behavior regardless of how messages were fetched.
     *
     * @param messages  Raw messages from the API (not yet tagged or normalized)
     * @param targetWindow  The intended window for these messages ('primary' or 'anchor')
     * @returns { merged: true } if a collision was detected and windows were merged
     */
    private ingestMessages = (
        messages: (APIDataRoomMessage | APIDataThreadMessage)[],
        targetWindow: 'primary' | 'anchor'
    ): { merged: boolean, effectiveWindow: 'primary' | 'anchor' } => {
        if (!messages.length) return { merged: false, effectiveWindow: targetWindow }

        let merged = false
        let effectiveWindow = targetWindow

        runInAction(() => {
            // ── Step 1: Symmetric collision detection (Issue 1 & 2) ──
            // Check if any fetched message already exists in the store with the
            // OTHER window's tag. No anchorMessageId guard — if there are no
            // anchor-tagged messages in the store, .some() simply returns false.
            const otherWindow = effectiveWindow === 'primary' ? 'anchor' : 'primary'
            const hasCollision = messages.some(msg => {
                const existing = this.messagesStore?.findById(msg.id)
                if (!existing) return false
                const existingWindow = (existing as DisplayRoomMessage | DisplayThreadMessage).window || 'primary'
                return existingWindow === otherWindow
            })

            // ── Step 3: Merge if collision ──
            if (hasCollision) {
                const primaryCount = this.getMessagesForContext('primary').length
                const anchorCount = this.getMessagesForContext('anchor').length
                debug(`${this.msgWallId} 💥 ingestMessages: collision detected (target=${effectiveWindow}) — primary:${primaryCount}, anchor:${anchorCount}, fetched:${messages.length} — merging`)

                this.mergeWindows()
                effectiveWindow = 'primary'
                merged = true
            }

            // ── Step 3: Retracted message normalization ──
            messages.forEach(message => {
                if (message.isRetracted) {
                    message.type = 'retracted'
                    message.ownerId = 1
                }
            })

            // ── Step 4: Window tagging ──
            messages.forEach(message => {
                (message as DisplayRoomMessage | DisplayThreadMessage).window = effectiveWindow
            })

            // ── Step 5: Store update ──
            this.messagesStore?.update(messages)
        })

        // ── Step 6: User fetching (fire-and-forget, outside runInAction) ──
        const ownerIds = messages.map(m => m.ownerId)
        if (this.modelType === 'room') {
            const forwardedOwnerIds = this.processForwardedMessage(messages as APIDataRoomMessage[])
            this.fetchUserService.fetchSpecificUsersFromRoom(this.modelId, [...ownerIds, ...forwardedOwnerIds])
        } else {
            this.fetchUserService.fetchSpecificUsersFromThread(this.modelId, ownerIds)
        }

        return { merged, effectiveWindow }
    }

    // ─── End Shared Post-Fetch Pipeline ────────────────────────────────

    /**
     * Retrieve messages based on the specified fetch type.
     * For 'older': Retrieve a number of messages prior to oldest message id that is not retracted.
     * For 'fresh': Retrieve all messages with updatedDates greater than the newestUpdatedDate including retracted.
     * For 'newer': Retrieve messages after the newest publishedDate (for filling gap toward primary window).
     *
     * KNOWN LIMITATION (accepted): a full page of messages that are all
     * invisible (e.g. 44+ consecutive retracted messages) produces no content
     * change, so the UI scroll triggers (FlatList onEndReached / Virtuoso
     * startReached) don't re-arm and the next page isn't fetched until the
     * user jiggles the scroll. Cursors remain correct throughout — see
     * libs/shared/test/message/MessageWallController.spec.ts (Case B).
     *
     * @param fetchType 'seed' | 'older' | 'newer' | 'fresh'
     */
    fetchMessages = async (fetchType: 'seed' | 'older' | 'newer' | 'fresh' = 'fresh') => {
        debug(
            `${this.msgWallId} 💬 fetchMessages: ${fetchType?.toUpperCase()}: ${this.modelType?.toUpperCase()}#${this.modelId} ::: ${
                this.isActive ? 'Active' : 'Inactive'
            }`
        )

        if (!this.modelId) {
            console.error('Missing a modelId! in MessageWallController.fetchMessages')
            return
        }

        this.errorFetching = null
        let count = this.messagesCount

        // Reset polling state when (re)initializing the message wall or fetching latest updates
        if (['seed', 'fresh'].includes(fetchType)) this.resetPoll()

        try {
            const sharedWhere = this.sharedMessageWhere

            // Fetch total count of messages if not available
            if (!this.messagesCount) {
                count = await this.fetchMessagesCount(sharedWhere)
            }

            /**
             * Determine which window this fetch targets:
             * - 'fresh' always targets the primary window (background polling)
             * - 'older' and 'newer' target the active window (user-driven scrolling)
             * - 'seed' targets the active window (determines which window the results are tagged into)
             */
            const targetWindow = fetchType === 'fresh' ? 'primary' : this.activeView

            if (targetWindow !== 'primary') {
                debug(`${this.msgWallId} 📌 fetchMessages targeting ${targetWindow} window (activeView=${this.activeView})`)
            }

            /**
             * Get messages we've already fetched from the backend for the target window.
             * Filter to positive IDs with an updatedDate — this excludes optimistic sends
             * (which use temporary negative IDs and have no updatedDate until server confirmation).
             */
            const persistedMessages = this.getMessagesForContext(targetWindow).filter(m => m.id > 0 && m.updatedDate)

            let whereClause: any
            let orderBy: string | undefined
            let limit: number | undefined

            // If we have nothing locally, force the seed behavior.
            if (!persistedMessages?.length) { fetchType = 'seed' }

            /**
             * Cursor selection notes:
             *
             * We display messages ordered by publishedDate. Attachments can cause "late publish":
             * a message can be created earlier, but only become visible later.
             *
             * Therefore:
             * - The visible timeline is driven by publishedDate, not id.
             *
             * We compute:
             * - oldestPublishedDate: for scrolling up (older messages)
             * - maxUpdatedDate: for polling changes within the current visible window
             *
             * NOTE: We intentionally DO NOT use an id tie-breaker here. If multiple rows share
             * the same timestamp, we may re-fetch or overlap a few, but:
             * - The store update() should de-dupe/merge by id anyway
             * - The view is re-sorted elsewhere
             * This keeps the query logic simpler and avoids brittle cursor edge cases.
             */
            let oldestPublishedDate: string | Date | undefined
            let maxUpdatedDate: string | Date | undefined

            if (persistedMessages.length) {
                for (const m of persistedMessages) {
                    // Oldest published cursor (for paging "up" toward older visible messages)
                    if (m.publishedDate) {
                        if (!oldestPublishedDate || m.publishedDate < oldestPublishedDate) {
                            oldestPublishedDate = m.publishedDate
                        }
                    }

                    // Newest updated cursor (for polling updates)
                    if (!maxUpdatedDate || m.updatedDate > maxUpdatedDate) {
                        maxUpdatedDate = m.updatedDate
                    }
                }
            }

            /**
             * Query shapes:
             *
             * 1) SEED:
             *    - Get the latest visible messages by published timeline.
             *    - ORDER BY publishedDate DESC
             *
             * 2) OLDER:
             *    - Keyset pagination backwards in the published timeline.
             *    - WHERE publishedDate < cursorPublishedDate
             *    - ORDER BY publishedDate DESC
             *
             * 3) NEWER:
             *    - Keyset pagination forwards in the published timeline (for anchor window gap filling).
             *    - WHERE publishedDate > newestPublishedDate
             *    - ORDER BY publishedDate ASC
             *
             * 4) FRESH:
             *    - Poll for updates/newly-published messages, but ONLY within the currently loaded window
             *      (to avoid pulling in a 1-year-old message that got a like and creating a huge "gap").
             *    - WHERE updatedDate > maxUpdatedDate
             *      AND publishedDate >= oldestPublishedDate
             *    - ORDER BY updatedDate ASC
             *
             * NOTE: This assumes publishedDate represents "when it became visible" (attachments done).
             */
            if (fetchType === 'seed'
                || (fetchType === 'older' && !oldestPublishedDate)
                || (fetchType === 'newer' && !persistedMessages?.length)
                || (fetchType === 'fresh' && (!maxUpdatedDate || !oldestPublishedDate) )
            ) {
                fetchType = 'seed'
                // First request (or hard refresh): latest visible messages by published timeline
                // The or 'older' bit is a maybe edgecase falling back to seed if no oldestPublishedDate
                orderBy = 'publishedDate DESC'
                whereClause = {} // no extra cursor constraints
                limit = this.limit
            } else if (fetchType === 'older') {
                // Fetch older messages than what we currently have loaded (by published timeline)
                orderBy = 'publishedDate DESC'
                limit = this.limit

                // Simple publishedDate keyset paging.
                // (We accept possible overlap if multiple messages share the same publishedDate.)
                whereClause = { publishedDate: { lte: oldestPublishedDate } }
            } else if (fetchType === 'newer') {
                // Fetch newer messages than what we currently have loaded (by published timeline).
                // Used for anchor window gap filling toward primary.
                orderBy = 'publishedDate ASC'
                limit = this.limit

                // Compute newestPublishedDate from persisted messages
                let newestPublishedDate: string | Date | undefined
                for (const m of persistedMessages) {
                    if (m.publishedDate) {
                        if (!newestPublishedDate || m.publishedDate > newestPublishedDate) {
                            newestPublishedDate = m.publishedDate
                        }
                    }
                }

                whereClause = { publishedDate: { gte: newestPublishedDate } }
            } else if (fetchType === 'fresh') {
                // Poll for anything newly updated since our last known maxUpdatedDate.
                // Keep it within the loaded window (publishedDate >= oldestPublishedDate) to prevent gaps.
                // If we lack cursors, treat as seed.
                orderBy = 'updatedDate ASC'
                limit = undefined // No limit to catch all updates; consider adding a safety cap if needed.

                whereClause = {
                    // Only consider updates inside/after the oldest visible published message we currently have loaded.
                    // This prevents pulling in very old messages (e.g., 1-year-old reaction) that would blow a hole in scrolling.
                    publishedDate: { gte: oldestPublishedDate },

                    // updatedDate cursor
                    updatedDate: { gt: maxUpdatedDate }
                }
            }

            const filter = {
                include: MESSAGE_DEFAULT_INCLUDE,
                unpublished: true, // keep client synchronized with backend state transitions (draft -> published, edits, reactions, etc.)
                where: {
                    ...sharedWhere,
                    ...whereClause
                },
                order: orderBy,
                limit: limit
            }

            const newMessages = await this.fetchMessagesFromAPI(filter)

            // Capture before ingest — a merge may change activeView to 'primary'
            const wasViewingPrimary = this.activeView === 'primary'

            // ─── Delegate to shared post-fetch pipeline ───────────────
            const { merged, effectiveWindow } = this.ingestMessages(newMessages, targetWindow)

            if (['fresh', 'seed'].includes(fetchType) && (newMessages.length || this.getCurrentUnreadCount() > 0)) {
                // Skip marking read if a merge just force-switched from anchor to primary —
                // the user was viewing historical messages and hasn't actually seen the new ones.
                // If the user was already on primary, a merge (cleaning up stale anchor remnants)
                // shouldn't prevent marking read.
                if (!merged || wasViewingPrimary) {
                    this.markAllMessagesAsRead()
                }
            }
        } catch (e) {
            runInAction(() => {
                this.errorFetching = e
            })
            throw e
        }
    }

    fetchMessagesCount = async (where?) => {
        try {
            const count = (await (this.modelType === 'room'
                ? this.api.Routes.Room.getMessagesCount(this.modelId, where)
                : this.api.Routes.Thread.getMessagesCount(this.modelId, where))).count

            runInAction(() => {
                this.messagesCount = count || 0
            })
        } catch (e) {
            runInAction(() => {
                this.messagesCount = 0
            })
        }

        return this.messagesCount
    }

    /**
     * This will fetch a list of rooms that haven't been fetched before.
     * Note: while this is ok for agency forwarded messages, it is not robust enough
     *       for forwarding between agencies
     * @param forwardedRoomIds
     * @returns
     */
    fetchDepartmentRooms = async (forwardedRoomIds: number[]) => {

        const existingRooms = this.roomStore.find(room => forwardedRoomIds.includes(room.id))
        const existingRoomIds = existingRooms.map(room => room.id)
        const unknownRoomIds = forwardedRoomIds.filter(id => !existingRoomIds.includes(id))

        if (!unknownRoomIds.length) return

        const filter = {
            where: {
                id: {
                    inq: unknownRoomIds
                }
            }
        }

        if (!this.room) return
        const rooms = await this.api.Routes.Department.getRooms(this.room.departmentId, filter)

        this.roomStore.update(rooms)
    }

    /**
     * This retrieves the Department of the forwarded message and returns a list of owner id's
     * @param newMessages
     * @returns an array of owner id's of the forwarded messages
     */
    processForwardedMessage = (newMessages: APIDataRoomMessage[]): number[] => {

        const forwardedOwnerIds = newMessages
            .filter(m => m.type === 'forwarded' && _.get(m, 'meta.forwardedMessage.ownerId'))
            .map(m => m.meta.forwardedMessage.ownerId)

        const forwardedRoomIds = newMessages
            .filter(m => m.type === 'forwarded' && _.get(m, 'meta.forwardedMessage.roomId'))
            .map(m => m.meta.forwardedMessage.roomId)
        this.fetchDepartmentRooms(forwardedRoomIds)

        return forwardedOwnerIds
    }

    // ─── Anchor Window / Navigation ────────────────────────────────────

    /**
     * Jump to a specific message. Handles three cases:
     * 1. Message exists in the primary window → switch to primary, return the message id for scrolling
     * 2. Message exists in the anchor window → return the message id for scrolling
     * 3. Message not loaded → create/replace anchor window around the target message
     *
     * @param messageId The message to jump to
     * @returns The messageId to scroll to, or null if there was an error
     */
    jumpToMessage = async (messageId: number): Promise<number | null> => {
        debug(`${this.msgWallId} 🎯 jumpToMessage: ${messageId}`)

        // Case 1: Message exists in primary window
        const primaryMessages = this.getMessagesForContext('primary')
        if (primaryMessages.some(m => m.id === messageId)) {
            debug(`${this.msgWallId} 🎯 jumpToMessage: msg#${messageId} found in primary, switching view → primary`)
            runInAction(() => {
                this.activeView = 'primary'
                this.anchorMessageId = null
            })
            return messageId
        }

        // Case 2: Message exists in current anchor window
        const anchorMessages = this.getMessagesForContext('anchor')
        if (anchorMessages.some(m => m.id === messageId)) {
            debug(`${this.msgWallId} 🎯 jumpToMessage: msg#${messageId} found in anchor, switching view → anchor`)
            runInAction(() => {
                this.activeView = 'anchor'
            })
            return messageId
        }

        // Case 3: Not loaded — seed a new anchor window around this message
        debug(`${this.msgWallId} 🎯 jumpToMessage: msg#${messageId} not loaded, seeding new anchor window`)
        const success = await this.seedAnchorWindow(messageId)
        return success ? messageId : null
    }

    /**
     * Seed an anchor window around a target message.
     * Fetches the target message first (to get its publishedDate), then fetches
     * ~22 messages before and ~22 after by publishedDate.
     *
     * If an anchor window already exists, its messages are cleared from the store first.
     *
     * Uses `ingestMessages` for post-fetch processing, which handles collision
     * detection automatically. If the fetched messages overlap with the primary
     * window, they merge and this method returns true but sets `activeView = 'primary'`
     * instead of 'anchor' (Issue 1 & 3 fix — no flicker, no orphaned anchor state).
     *
     * @returns true if the target message was found and ingested (whether as anchor or merged into primary),
     *          false if the target message was not found or not published in the current context.
     */
    seedAnchorWindow = async (messageId: number): Promise<boolean> => {
        debug(`${this.msgWallId} 🌱 seedAnchorWindow: ${messageId}`)

        // Replace any existing anchor window — only one anchor can exist at a time
        this.clearAnchorMessages()

        const halfLimit = Math.floor(this.limit / 2)
        const sharedWhere = this.sharedMessageWhere

        try {
            // Fetch the target message to get its publishedDate
            // Uses sharedWhere so messages from wrong room/repliesToId context are not found
            const targetMsgResults = await this.fetchMessagesFromAPI({
                include: MESSAGE_DEFAULT_INCLUDE,
                where: { ...sharedWhere, id: messageId },
                limit: 1
            })

            const targetMsg = targetMsgResults?.[0]

            if (!targetMsg?.publishedDate) {
                debug(`${this.msgWallId} 🌱 seedAnchorWindow: target message not found or not published in current context, msgId=${messageId}`)
                return false
            }

            // Fetch messages before and after the target in parallel
            const [before, after] = await Promise.all([
                this.fetchMessagesFromAPI({
                    include: MESSAGE_DEFAULT_INCLUDE,
                    where: { ...sharedWhere, publishedDate: { lt: targetMsg.publishedDate } },
                    order: 'publishedDate DESC',
                    limit: halfLimit
                }),
                this.fetchMessagesFromAPI({
                    include: MESSAGE_DEFAULT_INCLUDE,
                    where: { ...sharedWhere, publishedDate: { gt: targetMsg.publishedDate } },
                    order: 'publishedDate ASC',
                    limit: halfLimit
                })
            ])

            const allMessages = [...before, targetMsg, ...after]

            // ─── Delegate to shared post-fetch pipeline ───────────────
            const { merged, effectiveWindow } = this.ingestMessages(allMessages, 'anchor')

            runInAction(() => {
                this.activeView = effectiveWindow
                if (effectiveWindow === 'anchor') {
                    this.anchorMessageId = messageId
                    debug(`${this.msgWallId} 🌱 seedAnchorWindow complete: ${allMessages.length} messages loaded, view → anchor, anchorMsg#${messageId}`)
                } else {
                    // Collision: messages overlapped with primary and were merged.
                    this.anchorMessageId = null
                    debug(`${this.msgWallId} 🌱 seedAnchorWindow complete: ${allMessages.length} messages merged into primary, view → primary (collision), anchorMsg cleared`)
                }
            })

            return true
        } catch (e) {
            console.error('seedAnchorWindow failed:', e)
            throw e
        }
    }

    /**
     * Clear all anchor-tagged messages from the store.
     */
    clearAnchorMessages = () => {
        if (!this.messagesStore) return

        const anchorMessages = this.getMessagesForContext('anchor')

        if (anchorMessages.length) {
            debug(`${this.msgWallId} 🧹 clearAnchorMessages: removing ${anchorMessages.length} messages from store`)
        }

        runInAction(() => {
            for (const msg of anchorMessages) {
                this.messagesStore.deleteById(msg.id)
            }
        })
    }

    /**
     * Return to the primary (live) window. Used by "jump to present" button.
     * Keeps anchor window messages in the store so the user can jump back
     * without re-fetching, and so collision detection works if they scroll up.
     */
    jumpToPresent = () => {
        debug(`${this.msgWallId} ⬇️ jumpToPresent`)

        runInAction(() => {
            this._activeView = 'primary'
        })
    }

    /**
     * Merge the anchor window into primary. Called when collision is detected.
     *
     * Retags all anchor messages in the store to 'primary', switches activeView
     * to 'primary', and clears anchor state. The UI layers (web/mobile) are
     * responsible for handling the activeView transition gracefully:
     * - Web: defers followOutput activation by one animation frame
     * - Mobile: keeps RefreshControl always mounted to avoid layout disruption
     */
    private mergeWindows = () => {
        debug(`${this.msgWallId} 🔀 mergeWindows: retagging anchor → primary`)

        const anchorMessages = this.getMessagesForContext('anchor')

        runInAction(() => {
            for (const msg of anchorMessages) {
                msg.window = 'primary'
            }

            this._activeView = 'primary'
            this.anchorMessageId = null
        })

        debug(`${this.msgWallId} 🔀 mergeWindows complete: ${anchorMessages.length} messages retagged`)
    }

    // ─── End Anchor Window / Navigation ────────────────────────────────

    markAllMessagesAsRead = async () => {
        try {
            // Only mark as read when the app is active and viewing the primary (live) window
            if (!this.isActive || this.activeView !== 'primary') {
                debug(`${this.msgWallId} 🟩 markAllMessagesAsRead skipped: ${this.modelType.toUpperCase()}#${this.modelId} (active=${this.isActive}, view=${this.activeView})`)
                return
            }

            debug(`${this.msgWallId} ✅ markAllMessagesAsRead: ${this.modelType.toUpperCase()}#${this.modelId}|${this.repliesToId}`)

            if (this.modelType === 'room') {
                this.unreadState.markReadByRoomId(this.modelId, this.repliesToId)
            } else {
                this.unreadState.markReadByThreadId(this.modelId)
            }
        } catch (error) {
            debug('markAllMessagesAsRead', error.message)
        }
    }

    getCurrentUnreadCount = (): number => {
        if (this.modelType === 'room') {
            if (this.repliesToId) {
                // For replies, get the specific reply unread count
                const replyUnread = this.unreadState.getReplyUnread(this.modelId, this.repliesToId)
                return replyUnread?.unreadCount || 0
            } else {
                // For main room, get the room count minus replies count
                const roomUnread = this.unreadState.getRoomUnread(this.modelId)
                return roomUnread ? (roomUnread.count - roomUnread.repliesCount) : 0
            }
        } else {
            // For threads
            const threadUnread = this.unreadState.getThreadUnread(this.modelId)
            return threadUnread?.count || 0
        }
    }

    startPoll = () => {
        this.stopPoll()

        if (!PollConfig.globalEnabled) return

        this.pollInterval = setInterval(async () => {
            try {
                debug(`${this.msgWallId} ⏰ Poll running: ${this.modelType.toUpperCase()}#${this.modelId}`)
                await this.fetchMessages('fresh')
            } catch (error: any) {
                if (error.status === 401) {
                    this.stopPoll()
                }
            }

        }, this.getPollIntervalTime())
    }

    stopPoll() {
        if (this.pollInterval) {
            clearInterval(this.pollInterval)
            this.pollInterval = undefined
        }
    }

    getPollIntervalTime() {
        switch (this.modelType) {
            case 'room':
                return GET_ROOM_MESSAGE_POLL_INTERVAL_MS
            case 'thread':
            default:
                return GET_THREAD_MESSAGE_POLL_INTERVAL_MS
        }
    }

    resetPoll() {
        if (this.pollInterval) {
            this.startPoll()
        }
    }

    get messagesWithDates() {
        // constructs an array of messages with date objects inserted
        const messages = this.messagesVisible // excludes retracted messages
        let newMessagesWithDates = null

        runInAction(() => {
            newMessagesWithDates = messages.map((msg, idx) => {
                // add a date object every time the message date changes

                const newMsg = msg //{ ...msg }

                // if no publishedDate (new messages not yet posted to server) use createdDate
                const currentDateField = newMsg.publishedDate ? 'publishedDate' : 'createdDate'

                // Convert current message date to moment format for comparison
                const currentPostDate = moment(newMsg[currentDateField]).format('LL')

                if (idx === 0) {
                    // First message
                    newMsg.dateChanged = true
                } else {
                    const prevMsg = messages[idx - 1]
                    const prevDateField = prevMsg.publishedDate ? 'publishedDate' : 'createdDate'

                    // Convert previous message date to moment format
                    const prevPostDate = moment(prevMsg[prevDateField]).format('LL')

                    // Check if the date has changed
                    newMsg.dateChanged = prevPostDate !== currentPostDate

                    // Set previous message information
                    newMsg.prevMessageDate = prevMsg[prevDateField]
                    newMsg.prevOwnerId = prevMsg.ownerId
                }

                return newMsg
            })
        })

        return newMessagesWithDates
    }

    get messagesSorted() {
        // Default to showing only the active window's messages, sorted for rendering
        return this.getMessagesForContextSorted(this.activeView)
    }

    /**
     * Get messages for the current room/thread/repliesToId context, filtered to
     * a specific window. Unsorted — use when order doesn't matter (existence checks,
     * cursor computation, bulk retag/delete, counts).
     */
    getMessagesForContext(window: 'primary' | 'anchor' = this.activeView): Array<DisplayRoomMessage | DisplayThreadMessage> {
        if (!this.messagesStore) return []

        return this.messagesStore.find(m => {
            // Window filtering: only include messages tagged with the target window.
            // Untagged messages are treated as 'primary' (e.g. optimistic sends, legacy data).
            const msgWindow = m.window || 'primary'
            if (msgWindow !== window) return false

            if (m.threadId === this.modelId) return true

            if (m.roomId === this.modelId) {
                if (this.repliesToId === null) {
                    return m.repliesToId === undefined || m.repliesToId === null
                } else {
                    return this.repliesToId === m.repliesToId
                }
            }

            return false
        }) as Array<DisplayRoomMessage | DisplayThreadMessage>
    }

    /**
     * Messages for the current context, sorted ascending by `publishedDate`
     * (oldest → newest). Used for rendering (bottom-anchored chat) and anywhere
     * order matters.
     *
     * Includes unpublished, retracted, and empty messages —
     * use `messagesVisible` for display-safe filtering.
     */
    getMessagesForContextSorted(window: 'primary' | 'anchor' = this.activeView): Array<DisplayRoomMessage | DisplayThreadMessage> {
        return this.getMessagesForContext(window).slice().sort((a, b) => {
            const dateA = a.publishedDate ?? a.createdDate
            const dateB = b.publishedDate ?? b.createdDate
            return new Date(dateA).getTime() - new Date(dateB).getTime()
        })
    }

    get messagesVisible() {
        const sorted = this.messagesSorted?.filter((m) => {
            //the order of these if statements is significant

            //if there is a current upload nothing else matters, it should show
            const uploadableMedia = this.mediaUploadService.getMediaUploadsForModel(m.id, this.modelType)
            if (uploadableMedia.length && !m.isRetracted) return true

            // don't return unpublished messages
            if (!m.publishedDate) return false

            // don't return retracted messages
            if (m.isRetracted && (!(m as DisplayRoomMessage).hasReplies || m.meta?.repliesCount === 0)) return false

            // always display forwarded messages
            if (m.type === 'forwarded') return true

            // if empty, eg no text and no media, don't show
            if (!m.text && !m.media?.length) return false

            return true
        })

        return sorted
    }

    get room() {
        if (this.modelType === 'thread') return undefined
        return this.roomStore.findById(this.modelId)
    }

    get thread() {
        if (this.modelType === 'room') return undefined
        return this.threadStore.findById(this.modelId)
    }

}

decorate(injectable(), MessageWallController)
decorate(inject(Api), MessageWallController, 0)
decorate(inject(RoomStore), MessageWallController, 1)
decorate(inject(RoomMessagesStore), MessageWallController, 2)
decorate(inject(ThreadStore), MessageWallController, 3)
decorate(inject(ThreadMessagesStore), MessageWallController, 4)
decorate(inject(UnreadCountsState), MessageWallController, 5)
decorate(inject(MediaUploadService), MessageWallController, 6)
decorate(inject(FetchUserService), MessageWallController, 7)
decorate(inject(PushService), MessageWallController, 8)

export { MessageWallController }
